import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { UserInfo } from "../Components/atoms/atoms";
import "./technolive.css";
import logo from "../Asset/Technogenius-logo.png";

type Phase = "upcoming" | "active";
type Promotion = { phase: Phase; code: string; type: string; value: number; startsAt: string; expiresAt: string; serverNow: string; signedIn: boolean };
const dismissalKeys = { upcoming: "technolive-october-2026-upcoming-dismissed", active: "technolive-october-2026-dismissed" };
function readDismissals() {
  try { return { upcoming: sessionStorage.getItem(dismissalKeys.upcoming) === "yes", active: sessionStorage.getItem(dismissalKeys.active) === "yes" }; }
  catch { return { upcoming: false, active: false }; }
}
const excluded = /^\/(admin|checkout|payment|orders|account|login|register|forgot-password|reset-password|cart)(\/|$)/;
function OfferCountdown({ target, serverNow, upcoming }: { target: string; serverNow: string; upcoming: boolean }) {
  const duration = Date.parse(target) - Date.parse(serverNow);
  const [seconds, setSeconds] = useState(() => Math.max(0, Math.ceil(duration / 1000)));
  useEffect(() => {
    // Advance the server's time with a monotonic clock, independent of the device's date.
    const syncedAt = performance.now();
    const tick = () => setSeconds(Math.max(0, Math.ceil((duration - (performance.now() - syncedAt)) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [duration, serverNow]);
  const units: [string, number][] = [
    ["Days", Math.floor(seconds / 86400)],
    ["Hours", Math.floor(seconds % 86400 / 3600)],
    ["Minutes", Math.floor(seconds % 3600 / 60)],
    ["Seconds", seconds % 60],
  ];
  const label = upcoming ? "Offer starts in" : "Offer ends in";
  return <div className="technolive-countdown">
    <p className="technolive-countdown-label">{label}</p>
    <div className="technolive-countdown-units" role="timer" aria-live="off" aria-label={`${label} ${units.map(([label, value]) => `${value} ${value === 1 ? label.toLowerCase().slice(0, -1) : label.toLowerCase()}`).join(', ')}`}>
      {units.map(([label, value]) => <div key={label} aria-hidden="true"><strong>{String(value).padStart(2, "0")}</strong><span>{label}</span></div>)}
    </div>
  </div>;
}

export default function TechnolivePopup() {
  const { pathname } = useLocation();
  const user = useRecoilValue(UserInfo);
  const token = user?.access_token;
  const dialog = useRef<HTMLDialogElement>(null);
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [dismissed, setDismissed] = useState(readDismissals);
  const [copied, setCopied] = useState("");
  const hidden = excluded.test(pathname);
  const isDismissed = promotion ? dismissed[promotion.phase] : false;
  const hasPromotion = !!promotion;
  const upcoming = promotion?.phase === "upcoming";
  useEffect(() => {
    if (hidden || !process.env.REACT_APP_SERVER_URL) return;
    let alive = true;
    const abort = new AbortController();
    let expiry: ReturnType<typeof setTimeout>;
    async function refresh() {
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL!.replace(/\/$/, "")}/coupons/technolive`, {
          signal: abort.signal, credentials: "omit", cache: "no-store",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const result = response.ok ? await response.json() : null;
        const p = result?.data?.promotion;
        const remaining = p ? Date.parse(p.expiresAt) - Date.parse(p.serverNow) : 0;
        const valid = p?.code === "TECHNOLIVE" && p.type === "percent" && p.value === 7 &&
          Number.isFinite(Date.parse(p.startsAt)) && Date.parse(p.startsAt) < Date.parse(p.expiresAt) && remaining > 0 &&
          p.phase === (Date.parse(p.startsAt) > Date.parse(p.serverNow) ? "upcoming" : "active");
        if (!alive) return;
        clearTimeout(expiry);
        setPromotion(valid ? p : null);
        if (valid) {
          const untilBoundary = p.phase === "upcoming" ? Date.parse(p.startsAt) - Date.parse(p.serverNow) : remaining;
          expiry = setTimeout(() => { if (p.phase === "upcoming") void refresh(); else setPromotion(null); }, Math.min(untilBoundary, 2147483647));
        }
      } catch { if (alive) setPromotion(null); }
    }
    void refresh();
    const poll = setInterval(refresh, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; abort.abort(); clearInterval(poll); clearTimeout(expiry); document.removeEventListener("visibilitychange", onVisible); setPromotion(null); };
  }, [hidden, token]);
  useEffect(() => {
    const element = dialog.current;
    if (!element || !hasPromotion || isDismissed || hidden) return;
    const previous = document.activeElement as HTMLElement | null;
    element.showModal();
    return () => { element.close(); previous?.focus?.({ preventScroll: true }); };
  }, [hasPromotion, isDismissed, hidden]);
  function dismiss() {
    if (!promotion) return;
    setDismissed(old => ({ ...old, [promotion.phase]: true }));
    try { sessionStorage.setItem(dismissalKeys[promotion.phase], "yes"); } catch { /* Dismissal still works without storage. */ }
  }
  function openAtTop(event: React.MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    dismiss();
    // Reset after navigation and dialog cleanup have finished.
    requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  }
  async function copy() {
    try { await navigator.clipboard.writeText("TECHNOLIVE"); setCopied("Code copied. Paste it at checkout."); }
    catch { setCopied("Enter TECHNOLIVE in the coupon field at checkout."); }
  }
  if (!promotion || isDismissed || hidden) return null;
  return <dialog ref={dialog} className="technolive-popup" aria-labelledby="technolive-title" aria-describedby="technolive-terms" onCancel={e => { e.preventDefault(); dismiss(); }} onClick={e => { if (e.target === e.currentTarget) dismiss(); }}>
    <div className="technolive-top"><img className="technolive-logo" src={logo} alt="Technogenious Solutions" /><button type="button" className="technolive-close" onClick={dismiss} aria-label="Close coupon offer">×</button></div>
    <div className="technolive-body">
      <p className="technolive-eyebrow">{upcoming ? "Coming October 5" : "A welcome offer, just for you"}</p>
      <h2 id="technolive-title"><strong>7%</strong> OFF</h2>
      <p className="technolive-intro">{upcoming ? "Your welcome offer is coming." : "New account. A little extra savings."}</p>
      <p>{upcoming ? "Create your account October 5–9, then use this code at checkout to save on regular-price items:" : "Create your account and check out before October 10 to save on regular-price items:"}</p>
      <div className="technolive-code"><strong>TECHNOLIVE</strong><button type="button" onClick={copy}>Copy code</button></div>
      <p className="technolive-dates">October 5–9, 2026 · Jamaica time</p>
      <OfferCountdown target={upcoming ? promotion.startsAt : promotion.expiresAt} serverNow={promotion.serverNow} upcoming={upcoming} />
      <Link className="technolive-action" to={upcoming || promotion.signedIn ? "/products" : "/register"} onClick={openAtTop}>{upcoming ? "Browse products" : promotion.signedIn ? "Shop the offer" : "Create my account"}<span aria-hidden="true"> →</span></Link>
      {!upcoming && !promotion.signedIn && <Link className="technolive-signin" to="/login" onClick={dismiss}>Created your account during the offer? Sign in</Link>}
      <p id="technolive-terms">For accounts created October 5–9, 2026 only. Sign in and check out before October 10 at 12:00 a.m. (Jamaica time). One use per account. Sale items and delivery charges are excluded. Cannot be combined with another coupon.</p>
      <p className="technolive-copy-status" role="status">{copied}</p>
      <button type="button" className="technolive-later" onClick={dismiss}>Continue browsing</button>
    </div>
  </dialog>;
}
